/**
 * waap-overview-tab.tsx — Read-only Overview (Dashboard) tab for a WAAP service.
 *
 * Mirrors Partner's DashboardTab.tsx using ops-console primitives.
 * Renders the 30-day account-level BI dashboard from the BE pre-normalized
 * WaapChart[] discriminated union. Supports the "Refresh" button to bypass
 * the BE's 8h cache (re-GET with ?refresh=true).
 *
 * Data source:
 *   GET /waap/domains/:anchorId/dashboard/charts  (useActAsWAAPDashboardCharts)
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, CardTitle } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { actAsClient } from '@/api/act-as-client';
import {
  useActAsWAAPDashboardCharts,
  actAsWAAPKeys,
  type WaapChart,
  type WaapNumberChart,
  type WaapPieChart,
  type WaapTableChart,
  type WaapTimeseriesChart,
} from '@/api/act-as-waap';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { useQueryClient } from '@tanstack/react-query';

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function fmtNumber(value: number | null, unit: string): string {
  if (value == null) return '—';
  if (unit === 'bps') {
    if (value >= 1e9) return `${(value / 1e9).toFixed(2)} Gbps`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(2)} Mbps`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(2)} Kbps`;
    return `${value} bps`;
  }
  if (unit === 'B') {
    if (value >= 1e12) return `${(value / 1e12).toFixed(2)} TB`;
    if (value >= 1e9)  return `${(value / 1e9).toFixed(2)} GB`;
    if (value >= 1e6)  return `${(value / 1e6).toFixed(2)} MB`;
    return `${value} B`;
  }
  if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
  return String(value);
}

/* ── Sub-components ──────────────────────────────────────────────────────── */

function NumberWidget({ chart }: { chart: WaapNumberChart }) {
  if (chart.error) {
    return (
      <Card>
        <CardHeader><CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>{chart.title}</CardTitle></CardHeader>
        <CardBody><span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-xs)' }}>Unavailable</span></CardBody>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader><CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>{chart.title}</CardTitle></CardHeader>
      <CardBody>
        <span style={{ fontSize: 'var(--vn-font-size-2xl)', fontWeight: 700, color: 'var(--vn-fg-default)' }}>
          {fmtNumber(chart.value, chart.unit)}
        </span>
      </CardBody>
    </Card>
  );
}

function PieWidget({ chart }: { chart: WaapPieChart }) {
  const slices = chart.slices ?? [];
  return (
    <Card>
      <CardHeader><CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>{chart.title}</CardTitle></CardHeader>
      <CardBody>
        {chart.error || slices.length === 0 ? (
          <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-xs)' }}>
            {chart.error || 'No data'}
          </span>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            {slices.slice(0, 8).map((s) => (
              <div
                key={s.name}
                style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--vn-font-size-xs)', gap: 'var(--vn-space-3)' }}
              >
                <span style={{ color: 'var(--vn-fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                <span style={{ color: 'var(--vn-fg-default)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                  {s.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function TableWidget({ chart }: { chart: WaapTableChart }) {
  const cols = chart.columns ?? [];
  const rows = chart.rows ?? [];
  const columns = useMemo<ColumnDef<Record<string, unknown>, unknown>[]>(
    () =>
      cols.map((c) => ({
        id: c.key,
        accessorKey: c.key,
        header: c.label,
        cell: ({ row }: { row: { original: Record<string, unknown> } }) => String(row.original[c.key] ?? '—'),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cols.map((c) => c.key).join(',')],
  );
  return (
    <Card style={{ gridColumn: '1 / -1' }}>
      <CardHeader><CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>{chart.title}</CardTitle></CardHeader>
      <CardBody style={{ padding: 0 }}>
        {chart.error || rows.length === 0 ? (
          <div style={{ padding: 'var(--vn-space-4)', color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-xs)' }}>
            {chart.error || 'No data'}
          </div>
        ) : (
          <DataTable columns={columns} data={rows.slice(0, 10)} dense />
        )}
      </CardBody>
    </Card>
  );
}

function TimeseriesWidget({ chart }: { chart: WaapTimeseriesChart }) {
  const series = chart.series ?? [];
  const data   = chart.data   ?? [];
  return (
    <Card style={{ gridColumn: '1 / -1' }}>
      <CardHeader><CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>{chart.title}</CardTitle></CardHeader>
      <CardBody>
        {chart.error || data.length === 0 ? (
          <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-xs)' }}>
            {chart.error || 'No data'}
          </span>
        ) : (
          <div>
            <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)', marginBottom: 'var(--vn-space-2)' }}>
              {series.map((s) => s.label).join(' · ')} — {data.length} points
            </div>
            <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
              Chart visualization available in B2 Analytics wave.
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function ChartWidget({ chart }: { chart: WaapChart }) {
  if (chart.kind === 'number')     return <NumberWidget chart={chart} />;
  if (chart.kind === 'pie')        return <PieWidget chart={chart} />;
  if (chart.kind === 'table')      return <TableWidget chart={chart} />;
  if (chart.kind === 'timeseries') return <TimeseriesWidget chart={chart} />;
  return null;
}

/* ── Component ───────────────────────────────────────────────────────────── */

interface WaapOverviewTabProps {
  serviceId: string;
  anchorId: string;
}

/**
 * Overview tab — 30-day account-level BI dashboard (read-only).
 * KPI scalars, pie/table breakdowns, and timeseries trends.
 */
export function WaapOverviewTab({ serviceId, anchorId }: WaapOverviewTabProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);

  const chartsQ = useActAsWAAPDashboardCharts(anchorId);

  async function handleRefresh() {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await actAsClient.get<{ charts: WaapChart[] }>(
        `/waap/domains/${anchorId}/dashboard/charts?tz=${encodeURIComponent(tz)}&refresh=true`,
      );
      qc.setQueryData(actAsWAAPKeys.aspect(anchorId, 'dashboard/charts'), res.data);
    } catch {
      // If refresh fails, fall back to the stale cache — not a fatal error.
    } finally {
      setRefreshing(false);
    }
  }

  if (chartsQ.isLoading) return <TabLoading rows={6} />;
  if (chartsQ.isError) {
    return (
      <TabError
        title={t('waap_domain.overview_error', 'Failed to load dashboard')}
        onRetry={() => chartsQ.refetch()}
      />
    );
  }

  if (!chartsQ.data) {
    return (
      <EmptyState
        title={t('waap_domain.overview_not_configured', 'Dashboard not configured')}
        description={t(
          'waap_domain.overview_not_configured_desc',
          'The WAAP dashboard has not been configured for this service yet.',
        )}
      />
    );
  }

  const { charts } = chartsQ.data;
  const numberCharts    = charts.filter((c): c is typeof c & { kind: 'number' }     => c.kind === 'number');
  const nonNumberCharts = charts.filter((c) => c.kind !== 'number');

  // Suppress unused serviceId lint — kept in props for query invalidation context
  void serviceId;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* ── Refresh action ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw size={14} style={{ marginRight: 'var(--vn-space-1)' }} />
          {refreshing
            ? t('waap_domain.overview_refreshing', 'Refreshing…')
            : t('waap_domain.overview_refresh', 'Refresh (bypass cache)')}
        </Button>
      </div>

      {/* ── KPI scalars row ────────────────────────────────────────────── */}
      {numberCharts.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 'var(--vn-space-3)',
          }}
        >
          {numberCharts.map((c) => (
            <ChartWidget key={c.key} chart={c} />
          ))}
        </div>
      )}

      {/* ── Pies, tables, timeseries ───────────────────────────────────── */}
      {nonNumberCharts.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 'var(--vn-space-3)',
          }}
        >
          {nonNumberCharts.map((c) => (
            <ChartWidget key={c.key} chart={c} />
          ))}
        </div>
      )}

      {charts.length === 0 && (
        <Alert tone="info">
          {t('waap_domain.overview_no_charts', 'No dashboard charts returned by the server.')}
        </Alert>
      )}
    </div>
  );
}
